import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { DataFilterOptions } from '../../performance-data/data-filter-options.pd.model';
import { PerformanceDataViewType } from '../../performance-data/metric.pd.model';
import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { PeriodType } from '../../widget/date-range.widget.model';
import { PerformanceMetricData } from '../models/performance-metric-data.model';
import { ActionType } from '../my-performance-card.action';
import { MyPerformanceCardService } from '../my-performance-card.service';
import { PerformanceCardState } from '../my-performance-card.state';

import {
    GetPerformanceDataAction,
    getPerformanceDataResult,
    GetPerformanceDataResultAction,
} from './performance-data.action';

const getPerformanceDataEpic = (
    action: ActionsObservable<
        PayloadAction<ActionType, GetPerformanceDataAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
): Observable<PayloadAction<ActionType, GetPerformanceDataResultAction>> =>
    action.pipe(
        ofType(ActionType.MY_PERFORMANCE_CARD__GET_PERFORMANCE_DATA),
        switchMap((act) => {
            const service = deps.injector.get<MyPerformanceCardService>(
                MyPerformanceCardService,
            );
            const rootState = pluck<PerformanceCardState>(
                state.value,
                act.storePath,
            );
            const userId = act.payload.userId;
            const metricId = act.payload.metricId;
            const queueId = act.payload.queueId;
            const total = rootState.userData[userId]?.performanceCard?.total;
            const metricIds =
                metricId != null
                    ? [metricId]
                    : total?.colourGroups
                          ?.flatMap(
                              (group) =>
                                  group.metrics?.map(
                                      (metric) => metric.metricID,
                                  ),
                          )
                          ?.filter((id): id is number => id !== undefined);

            return service.listHierarchyUserInfo([userId]).pipe(
                switchMap((hierarchyInfo) => {
                    const roleId = hierarchyInfo[0]?.roleId;
                    const superGroupId = hierarchyInfo[0]?.superGroupId;

                    return service.listCacheDates(PeriodType.DAY, userId).pipe(
                        switchMap((cacheDates) => {
                            const sortedDates = cacheDates.sort(
                                (a, b) =>
                                    new Date(a.startDate).getTime() -
                                    new Date(b.startDate).getTime(),
                            );

                            const oldestStartDate = new Date(
                                sortedDates[0].startDate,
                            );
                            const latestEndDate = new Date(
                                sortedDates[sortedDates.length - 1].endDate,
                            );

                            const dataFilterOptions: DataFilterOptions = {
                                roleId,
                                superGroupIdList: [superGroupId],
                                date: latestEndDate,
                                metricIdList: metricIds,
                                viewType:
                                    queueId != null
                                        ? PerformanceDataViewType.USER_QUEUE
                                        : PerformanceDataViewType.USER_SUPER_GROUP,
                                displayNameCriteriaIdType: null,
                                outputCriteriaIdType: null,
                                periodType: PeriodType.DAY,
                                userIdList: [userId],
                                startDate: oldestStartDate,
                                managerIdList: [],
                                queueIdList: queueId != null ? [queueId] : [],
                                peerGroupIdList: [],
                                siteIdList: [],
                                areaIdList: [],
                                regionIdList: [],
                                lobIdList: [],
                            };
                            return service.listData(dataFilterOptions).pipe(
                                map(
                                    (
                                        performanceData: PerformanceMetricData[],
                                    ) =>
                                        getPerformanceDataResult(
                                            {
                                                ...act.payload,
                                                performanceData,
                                                userId,
                                                metricId,
                                                queueId,
                                            },
                                            act.storePath,
                                        ),
                                ),
                            );
                        }),
                    );
                }),
                catchError(() =>
                    of(
                        getPerformanceDataResult(
                            { ...act.payload, performanceData: [] },
                            act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

export const myPerformanceCardEpics = [getPerformanceDataEpic];
